import { Capacitor } from '@capacitor/core';

/**
 * Turn a video Blob/File into a URL a <video> element can actually render.
 *
 * On iOS WKWebView (Capacitor), `blob:` URLs for <video> are unreliable — they
 * frequently render a black box. So on native we write the bytes to a temp cache
 * file and use Capacitor.convertFileSrc() (a real, WKWebView-loadable URL). On
 * the web, a blob URL is fine.
 *
 * Returns the src plus a cleanup() to release it (revoke the blob URL / delete
 * the temp file). Always call cleanup when replacing or discarding the preview.
 */
export async function videoBlobToSrc(
  blob: Blob,
  ext = 'mp4',
): Promise<{ src: string; cleanup: () => void }> {
  if (!Capacitor.isNativePlatform()) {
    const url = URL.createObjectURL(blob);
    return { src: url, cleanup: () => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } } };
  }

  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const base64 = await blobToBase64(blob);
    const name = `yap-preview-${Date.now()}.${ext}`;
    await Filesystem.writeFile({ path: name, data: base64, directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path: name, directory: Directory.Cache });
    const src = Capacitor.convertFileSrc(uri);
    return {
      src,
      cleanup: () => { Filesystem.deleteFile({ path: name, directory: Directory.Cache }).catch(() => {}); },
    };
  } catch {
    // Fall back to a blob URL if the filesystem write fails.
    const url = URL.createObjectURL(blob);
    return { src: url, cleanup: () => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } } };
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => {
      const res = String(r.result || '');
      resolve(res.split(',')[1] || ''); // strip "data:...;base64,"
    };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
